/* Copyright (c) 2022-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { LoggingService, Logger } from '../logging/logging.service';
import { AudioEventStatus, AudioTelemetryData } from './audio-play.model';

@Injectable({
    providedIn: 'root'
})
export class AudioPlayService {
    private logger: Logger;
    private audioContextMap = new Map<string, HTMLAudioElement>();
    private audioSubject: ReplaySubject<AudioTelemetryData> = new ReplaySubject<AudioTelemetryData>(1);

    constructor(private loggingService: LoggingService) {
        this.logger = this.loggingService.getLogger('AudioPlayService');
    }

    getAudioTelemetryData(): Observable<AudioTelemetryData> {
        return this.audioSubject.asObservable();
    }

    playAudio(type: string, audioScenario: string) {
        const audio = this.audioContextMap.get(type);
        if (audio) {
            this.logger.info(`Playing audio ${type}`);
            audio
                .play()
                ?.then(_ => {
                    this.logger.info(`Played audio ${type}`);
                    this.audioSubject.next({ eventScenario: audioScenario, eventStatus: AudioEventStatus.Played });
                })
                .catch(err => {
                    this.logger.error(`Error while playing Audio  ${type} - ${err}`);
                    this.audioSubject.next({
                        eventScenario: audioScenario,
                        eventStatus: AudioEventStatus.ClientError,
                        errorDetails: err ?? ''
                    });
                });
        } else {
            this.logger.error(`audio type ${type} is not registered.`);
            this.audioSubject.next({ eventScenario: audioScenario, eventStatus: AudioEventStatus.NotRegistered });
        }
    }

    registerAudio(type: string, src: string, audioScenario: string) {
        if (!src) {
            this.logger.error(`Invalid src param for audio ${type}`);
            return;
        }
        if (!this.audioContextMap.has(type)) {
            this.logger.info(`Registering audio ${type}.`);
            this.audioContextMap.set(type, new Audio(src));
            this.audioSubject.next({ eventScenario: audioScenario, eventStatus: AudioEventStatus.Registered });
        } else {
            this.logger.error(`audio type ${type} is already registered.`);
        }
    }

    removeAudio(type: string) {
        if (this.audioContextMap.has(type)) {
            this.logger.info(`Removing audio of type: ${type}`);
            this.audioContextMap.delete(type);
        }
    }
}
